/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  InputBase,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useSnackbar } from "notistack";
import * as orgAdmin from "../../api/orgAdmin";
import { formatBytes } from "../apps/drive/format";

const AdminMembers = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState({ loading: true, members: [], error: null });
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      setState({ loading: false, members: await orgAdmin.listMembers(), error: null });
    } catch (err) {
      setState({ loading: false, members: [], error: err.message || "Could not load members." });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return state.members;
    return state.members.filter(
      (m) =>
        (m.user_full_name || "").toLowerCase().includes(q) ||
        (m.user_email || "").toLowerCase().includes(q)
    );
  }, [state.members, query]);

  const changeRole = async (member, role) => {
    try {
      const updated = await orgAdmin.setMemberRole(member.user_id, role);
      // Patch the one row rather than refetching every member and their storage
      // totals; the response already carries the new role.
      setState((prev) => ({
        ...prev,
        members: prev.members.map((m) =>
          m.user_id === member.user_id ? { ...m, role: updated.role ?? role } : m
        ),
      }));
      enqueueSnackbar(`${member.user_email || "Member"} is now ${role}.`, { variant: "success" });
    } catch (err) {
      // The API refuses to demote the last admin. Resync here specifically —
      // the Select has already moved to a value the server rejected.
      enqueueSnackbar(err.message || "Could not change the role.", { variant: "error" });
      load();
    }
  };

  if (state.loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: "accent.main" }} />
      </Box>
    );
  }

  const totalBytes = state.members.reduce((sum, m) => sum + Number(m.bytes_used || 0), 0);

  return (
    <Box sx={{ p: 3, display: "flex", gap: 2.5, alignItems: "flex-start" }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            mb: 2,
            height: 36,
            maxWidth: 420,
            borderRadius: "8px",
            border: `1px solid ${theme.palette.border.color}`,
            backgroundColor: theme.palette.secondary.main,
          }}
        >
          <SearchIcon sx={{ fontSize: 18, color: theme.palette.secondaryText.color }} />
          <InputBase
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or email"
            sx={{ flex: 1, fontSize: 13.5, color: theme.palette.text.color }}
          />
        </Box>

        {state.error && <Alert severity="error" sx={{ mb: 2 }}>{state.error}</Alert>}

        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell width={150}>Role</TableCell>
                <TableCell width={120}>Files</TableCell>
                <TableCell width={140}>Storage used</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((m) => (
                <TableRow
                  key={m.user_id}
                  hover
                  onClick={() => navigate(`/admin/members/${m.user_id}`)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                      <Avatar sx={{ width: 30, height: 30, fontSize: 13, bgcolor: "accent.solid" }}>
                        {(m.user_full_name || m.user_email || "?").charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 600 }} noWrap>
                            {m.user_full_name || m.user_email || m.user_id}
                          </Typography>
                          {m.role === "admin" && (
                            <Chip size="small" label="Org Admin" sx={{ fontSize: 10, height: 18 }} />
                          )}
                        </Box>
                        <Typography sx={{ fontSize: 12, color: theme.palette.secondaryText.color }} noWrap>
                          {m.user_email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  {/* stopPropagation so a quick role change doesn't also navigate away */}
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Select
                      size="small"
                      value={m.role}
                      onChange={(e) => changeRole(m, e.target.value)}
                      sx={{ fontSize: 13, minWidth: 120 }}
                    >
                      <MenuItem value="member" sx={{ fontSize: 13 }}>Member</MenuItem>
                      <MenuItem value="admin" sx={{ fontSize: 13 }}>Admin</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>{m.file_count}</TableCell>
                  <TableCell>{formatBytes(Number(m.bytes_used || 0))}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      {/* Summary rail, mirroring the WorkDrive members page. */}
      <Paper
        variant="outlined"
        sx={{ width: 260, flexShrink: 0, p: 2.5, borderRadius: "12px", backgroundColor: theme.palette.secondary.main }}
      >
        <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 1.5 }}>Organisation</Typography>
        <Typography sx={{ fontSize: 13, color: theme.palette.secondaryText.color, mb: 2 }}>
          {state.members.length} member{state.members.length === 1 ? "" : "s"},{" "}
          {state.members.filter((m) => m.role === "admin").length} admin
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: theme.palette.secondaryText.color }}>
          Total storage
        </Typography>
        <Typography sx={{ fontSize: 20, fontWeight: 600 }}>{formatBytes(totalBytes)}</Typography>
      </Paper>
    </Box>
  );
};

export default AdminMembers;
